import { useMemo } from 'react';

export type TranslationLocales = {
  readonly fallback: {
    readonly code: string;
    readonly label: string;
  };
  readonly supported: ReadonlyArray<{
    readonly code: string;
    readonly label: string;
  }>;
  readonly all: ReadonlyArray<string>;
};

type HookState = {
  readonly data: TranslationLocales | null;
  readonly loading: boolean;
  readonly error: string | null;
};

const STATIC_LOCALES: TranslationLocales = Object.freeze({
  fallback: { code: 'en', label: 'English' },
  supported: [
    { code: 'uk', label: 'Українська' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'pt', label: 'Português' },
    { code: 'ru', label: 'Русский' },
    { code: 'it', label: 'Italiano' },
    { code: 'de', label: 'Deutsch' },
  ],
  all: ['en', 'uk', 'es', 'fr', 'pt', 'ru', 'it', 'de'],
});

type HookResult = HookState & {
  readonly labelsByCode: Record<string, string> | null;
};

export const useTranslationLocales = (): HookResult => {
  const state: HookState = {
    data: STATIC_LOCALES,
    loading: false,
    error: null,
  };

  const labelsByCode = useMemo(() => {
    if (!state.data) {
      return null;
    }

    const entries = [[state.data.fallback.code, state.data.fallback.label] as const, ...state.data.supported.map((entry) => [entry.code, entry.label] as const)];
    return Object.fromEntries(entries);
  }, [state.data]);

  return { ...state, labelsByCode };
};

export const __internal = {
  resetCache: () => undefined,
};
